import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useUser } from "@/components/UserProvider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  skillsQueryOptions,
  skillsQueryKey,
  skillDetailQueryOptions,
} from "@/lib/data/react-query/skills";
import { deleteSkill } from "@/lib/data/requests/skills";
import type { SkillScope, SkillSummary } from "@/lib/data/schemas/skills";
import { SkillEditor } from "./SkillEditor";
import { automationError } from "@/components/features/automations/automationError";
export function SkillsPage() {
  const { hasPagePermission } = useUser();
  const scopes: SkillScope[] = [
    ...(hasPagePermission("skills.personal") ? ["personal" as const] : []),
    ...(hasPagePermission("skills.firm") ? ["firm" as const] : []),
  ];
  return scopes.length ? (
    <SkillsContent scopes={scopes} />
  ) : (
    <p className="p-8">You don’t have permission to manage skills.</p>
  );
}
function SkillsContent({ scopes }: { scopes: SkillScope[] }) {
  const [scope, setScope] = useState(scopes[0]);
  const [panel, setPanel] = useState<{
    kind: "edit" | "delete";
    value?: SkillSummary;
  }>();
  const list = useInfiniteQuery(skillsQueryOptions(scope));
  const detail = useQuery({
    ...skillDetailQueryOptions(panel?.value?.id ?? ""),
    enabled: panel?.kind === "edit" && !!panel.value,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const client = useQueryClient();
  const remove = useMutation({
    mutationFn: (skill: SkillSummary) => deleteSkill(skill.id, skill.revision),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: skillsQueryKey });
      setPanel(undefined);
    },
  });
  return (
    <main className="h-full overflow-auto p-6 md:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Skills</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Reusable instructions that help the agent handle specific kinds of
              work.
            </p>
          </div>
          <Button
            onClick={() => {
              setPanel({ kind: "edit" });
            }}
          >
            Create skill
          </Button>
        </div>
        <div className="flex gap-2" role="group" aria-label="Skill scope">
          {scopes.map((value) => (
            <Button
              key={value}
              variant={value === scope ? "default" : "outline"}
              aria-pressed={value === scope}
              onClick={() => {
                setScope(value);
              }}
            >
              {value === "firm" ? "Firm skills" : "Personal skills"}
            </Button>
          ))}
        </div>
        {list.isPending && <p role="status">Loading skills…</p>}
        {list.error && <p role="alert">{automationError(list.error)}</p>}
        {list.data?.pages
          .flatMap((page) => page.items)
          .map((skill) => (
            <article
              key={skill.id}
              className="flex items-start justify-between gap-4 rounded-xl border p-4"
            >
              <div className="min-w-0">
                <h2 className="font-medium">
                  {skill.name}
                  <span className="text-muted-foreground ml-3 text-xs">
                    {skill.enabled ? "Enabled" : "Disabled"}
                  </span>
                </h2>
                <p className="text-muted-foreground mt-2 text-sm break-words whitespace-pre-wrap">
                  {skill.description}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPanel({ kind: "edit", value: skill });
                  }}
                  aria-label={`Edit ${skill.name}`}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    remove.reset();
                    setPanel({ kind: "delete", value: skill });
                  }}
                  aria-label={`Delete ${skill.name}`}
                >
                  Delete
                </Button>
              </div>
            </article>
          ))}
        {list.data && !list.data.pages.some((page) => page.items.length) && (
          <p className="text-muted-foreground py-8 text-sm">
            No skills yet. Create one to give the agent reusable instructions.
          </p>
        )}
        {list.hasNextPage && (
          <Button
            variant="outline"
            disabled={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Load more
          </Button>
        )}
      </div>
      <Dialog
        open={!!panel}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setPanel(undefined);
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-auto sm:max-w-2xl"
          aria-describedby={
            panel?.kind === "delete" ? "skill-delete-description" : undefined
          }
        >
          <DialogHeader>
            <DialogTitle>
              {panel?.kind === "delete"
                ? "Delete skill"
                : panel?.value
                  ? "Edit skill"
                  : "Create skill"}
            </DialogTitle>
            {panel?.kind === "delete" && (
              <DialogDescription id="skill-delete-description">
                This skill will no longer be available to the agent.
              </DialogDescription>
            )}
          </DialogHeader>
          {panel?.kind === "edit" &&
            (!panel.value ? (
              <SkillEditor
                initialScope={scope}
                scopes={scopes}
                onClose={() => {
                  setPanel(undefined);
                }}
              />
            ) : detail.isPending || detail.isFetching ? (
              <p role="status">Loading skill…</p>
            ) : detail.error ? (
              <p role="alert">{automationError(detail.error)}</p>
            ) : (
              <SkillEditor
                key={`${detail.data.id}:${detail.data.revision}`}
                skill={detail.data}
                initialScope={scope}
                scopes={scopes}
                onClose={() => {
                  setPanel(undefined);
                }}
              />
            ))}
          {panel?.kind === "delete" && panel.value && (
            <>
              <p>Delete “{panel.value.name}”?</p>
              {remove.error && (
                <p role="alert" className="text-destructive">
                  {automationError(remove.error)}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  disabled={remove.isPending}
                  onClick={() => {
                    setPanel(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (panel.value) remove.mutate(panel.value);
                  }}
                >
                  Delete skill
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
